import { GroupedListItem, Octopus } from '@maximizer/core/shared/domain';
import { formatIsoDateOnly, toUtcSecondsIso } from '@maximizer/shared/util';
import { differenceInCalendarDays, subDays } from 'date-fns';
import {
  AppointmentFieldOption,
  AppointmentForm,
  AppointmentRecurrence,
  AppointmentSummary,
  CalendarAttendee,
  OutlookCalendarEvent,
  OutlookCalendarEventSummary,
  WallTimeToInstant,
} from '../../../models';

const RECURRING_FREQUENCY: Record<
  AppointmentRecurrence['repeat'],
  Octopus.AppointmentRecurringFrequency
> = {
  daily: Octopus.AppointmentRecurringFrequency.Daily,
  weekly: Octopus.AppointmentRecurringFrequency.Weekly,
  monthly: Octopus.AppointmentRecurringFrequency.DayOfMonth,
  yearly: Octopus.AppointmentRecurringFrequency.Yearly,
};

const DEFAULT_DAY_OF_WEEK = [0];
const NO_OCCURRENCE_DETAIL = 0;

function toServerZeroBased(oneBased: number): number {
  return oneBased - 1;
}

function recurringOccurrence(recurrence: AppointmentRecurrence): number {
  switch (recurrence.repeat) {
    case 'weekly':
      return toServerZeroBased(recurrence.interval);
    case 'monthly':
      return toServerZeroBased(recurrence.startsOn.getDate());
    default:
      return NO_OCCURRENCE_DETAIL;
  }
}

function recurringDayOfWeek(recurrence: AppointmentRecurrence): number[] {
  return recurrence.repeat === 'weekly'
    ? [recurrence.startsOn.getDay()]
    : DEFAULT_DAY_OF_WEEK;
}

export function mapRecurrenceToRecurringPattern(
  recurrence: AppointmentRecurrence,
): Octopus.AppointmentRecurringPattern {
  return {
    startDate: formatIsoDateOnly(recurrence.startsOn),
    endDate: formatIsoDateOnly(recurrence.endsOn),
    frequency: RECURRING_FREQUENCY[recurrence.repeat],
    occurence: recurringOccurrence(recurrence),
    dayOfWeek: recurringDayOfWeek(recurrence),
    skipWeekend: recurrence.skipWeekends,
    moveToWeekday: false,
  };
}

function toEntryKeys(
  attendees: CalendarAttendee[],
  type: CalendarAttendee['type'],
): { Key: { Value: string } }[] {
  return attendees
    .filter((attendee) => attendee.type === type && attendee.id)
    .map((attendee) => ({ Key: { Value: attendee.id as string } }));
}

export function mapCalendarEventToAppointmentDates(
  event: OutlookCalendarEventSummary,
  toInstant: WallTimeToInstant,
): {
  StartDate: string;
  EndDate: string;
} {
  if (!event.isAllDay) {
    return {
      StartDate: toUtcSecondsIso(toInstant(event.start)),
      EndDate: toUtcSecondsIso(toInstant(event.end)),
    };
  }

  const endsOnFollowingMidnight =
    differenceInCalendarDays(event.end, event.start) >= 1;
  const lastDay = endsOnFollowingMidnight ? subDays(event.end, 1) : event.end;

  return {
    StartDate: formatIsoDateOnly(event.start),
    EndDate: formatIsoDateOnly(lastDay),
  };
}

export function mapCalendarEventToAppointmentCreate(
  event: OutlookCalendarEvent,
  attendees: CalendarAttendee[],
  currentUserKey: string,
  location: Octopus.AppointmentLocationWrite | null,
  toInstant: WallTimeToInstant,
): Octopus.AppointmentCreate {
  return {
    Key: null,
    Subject: event.subject,
    ...mapCalendarEventToAppointmentDates(event, toInstant),
    Location: location,
    Organizer: Octopus.CurrentUser,
    Creator: Octopus.CurrentUser,
    DescriptionRichText: event.description,
    InteractionCategory: null,
    Result: null,
    Private: event.isPrivate,
    Users: [currentUserKey],
    AbEntries: toEntryKeys(attendees, 'abentry'),
    Leads: toEntryKeys(attendees, 'lead'),
  };
}

export class AppointmentFieldOptionMapper extends Octopus.ConfigurationMapper<
  Octopus.AppointmentFieldOptionsResponse,
  AppointmentFieldOption
> {
  from(
    source: Octopus.AppointmentFieldOptionsResponse,
  ): AppointmentFieldOption {
    const appointment = source.Appointment?.FieldOptions;

    const result = {
      categories:
        appointment?.InteractionCategory?.map(this.mapKeyAndDisplay) ?? [],
      results: appointment?.Result?.map(this.mapKeyAndDisplay) ?? [],
      locations: appointment?.Location?.map(this.mapKeyAndDisplay) ?? [],
    };
    return result;
  }
}
export class AppointmentFormMapper extends Octopus.ConfigurationMapper<
  AppointmentForm,
  Octopus.AppointmentCreate
> {
  constructor(private readonly toInstant: WallTimeToInstant) {
    super();
  }

  combineDateAndTime(date: Date, time: Date): Date {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();

    const combinedDateTime: Date = new Date(
      year,
      month,
      day,
      hours,
      minutes,
      seconds,
    );

    return combinedDateTime;
  }
  extractAttendees(
    source: GroupedListItem<string>[],
    type: string,
  ): { Key: { Value: string } }[] {
    return source
      .filter((o) => o.group == type)
      .map((o) => ({ Key: { Value: o.id } }));
  }

  // The form captures a single date, so an all-day appointment spans that day.
  private mapDates(source: AppointmentForm): {
    StartDate: string;
    EndDate: string;
  } {
    if (source.isAllDay) {
      const date = formatIsoDateOnly(source.date);
      return { StartDate: date, EndDate: date };
    }

    return {
      StartDate: toUtcSecondsIso(
        this.toInstant(this.combineDateAndTime(source.date, source.startTime)),
      ),
      EndDate: toUtcSecondsIso(
        this.toInstant(this.combineDateAndTime(source.date, source.endTime)),
      ),
    };
  }

  override from(source: AppointmentForm): Octopus.AppointmentCreate {
    const result = {
      Key: source.key,
      Subject: source.subject,
      ...this.mapDates(source),
      Location: source.location,
      Organizer: source.assignedUser ?? Octopus.CurrentUser,
      Creator: source.creator ?? Octopus.CurrentUser,
      DescriptionRichText: source.description,
      InteractionCategory: source.category,
      Result: source.result,
      Private: source.isPrivate,
      Users:
        source.attendees?.filter((o) => o.group == 'user').map((o) => o.id) ??
        [],
      AbEntries: this.extractAttendees(source.attendees ?? [], 'abentry'),
      Leads: this.extractAttendees(source.attendees ?? [], 'lead'),
    };
    return result;
  }
}

export class AppointmentSummaryMapper extends Octopus.ConfigurationMapper<
  Octopus.AppointmentSummaryResponse,
  AppointmentSummary
> {
  override from(
    source: Octopus.AppointmentSummaryResponse,
  ): AppointmentSummary {
    const appointmentObj =
      source.Appointment.Data?.length > 0 ? source.Appointment.Data[0] : null;

    const appointmentAmount =
      source['Appointment.Count']?.Data?.length > 0
        ? source['Appointment.Count'].Data[0]
        : null;

    const result: AppointmentSummary = {
      appointmentAmount: appointmentAmount?.['$COUNT()'] ?? 0,
    };

    if (appointmentObj) {
      result.appointment = {
        key: appointmentObj.Key,
        subject: appointmentObj.Subject,
        startDate: appointmentObj.StartDate,
        endDate: appointmentObj.EndDate,
        description: appointmentObj.DisplayValue,
        location: appointmentObj.Location,
        creationDate: appointmentObj.CreationDate,
        users: (appointmentObj.Users ?? []).map((user) => user.DisplayValue),
        abEntries: (appointmentObj.AbEntries ?? []).map((abentry) => ({
          firstName: abentry.FirstName ?? '',
          lastName: abentry.LastName ?? '',
          type: abentry.Type ?? '',
          companyName: abentry.CompanyName ?? '',
        })),
        leads: (appointmentObj.Leads ?? []).map((lead) => ({
          firstName: lead.FirstName ?? '',
          lastName: lead.LastName ?? '',
        })),
      };
    }
    return result;
  }
}
